import { BillingApi, Configuration } from '@/api';
import { InvoiceTransactionFilters } from '@/api/BillingApi';
import { InvoiceTransactions } from '@/models/Invoice';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState: InvoiceTransactions = {
  totalCount: 0,
  estimatedTotalCount: 0,
  items: [],
};

export const invoiceTransactionsQueryKey = ['invoice-transactions'] as const;

interface FindInvoiceTransactionsQueryResult {
  invoiceTransactions: InvoiceTransactions;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  itemsPerPage: number;
  invalidateQuery: () => Promise<void>;
}

const useFindInvoiceTransactionsQuery = (
  page: number,
  filters?: Partial<InvoiceTransactionFilters>,
): FindInvoiceTransactionsQueryResult => {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const billingApi = new BillingApi(new Configuration(auth));

  const itemsPerPage = 10;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [...invoiceTransactionsQueryKey, page, filters],
    queryFn: () => {
      return billingApi.listInvoiceTransactions({
        invoiceId: filters?.invoiceId,
        status: filters?.status,
        limit: itemsPerPage,
        offset: page * itemsPerPage - itemsPerPage,
      });
    },
  });

  const invalidateQuery = async () => {
    return queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === invoiceTransactionsQueryKey[0],
    });
  };

  return {
    invoiceTransactions: data ?? initialState,
    isLoading,
    isFetching,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useFindInvoiceTransactionsQuery;
